import React from "react";
import { useAppSelector } from "store/hooks";
import { ErrorMessage } from "@hookform/error-message";
import { Stack, Box, styled, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { ReactComponent as EmailIcon } from "images/check_email.svg";
import { HelpIcon } from "UI/HelpIcon";

import {
  usePasswordRecoveryPage,
  passwordRecoverySteps,
} from "./PasswordRecoveryPage.hook";
import {
  AuthContainer,
  AuthFormContent,
  AuthLink,
  AuthButtonLink,
  Input,
  InputLabel,
  SubmitButton,
  Error,
} from "SC";
import * as FP from "utils/fp-js";
import { customColors } from "models/customColors";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  getCodeValidation,
  getPasswordConfirmValidation,
  passwordValidation,
} from "models/validations";
import { FormTextField } from "UI/FormTextField";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { DevIndicator } from "./DevIndicator";

const Title = styled(Box)`
  color: ${customColors.black};
  font-size: 26px;
  font-family: Inter;
  font-weight: 700;
  word-wrap: break-word;
`;

const Info = styled(Box)`
  color: ${customColors.black};
  font-size: 18px;
  font-family: Inter;
  font-weight: 400;
  word-wrap: break-word;
  text-align: left;
`;

const Question = styled(Box)`
  color: ${customColors.grayText};
  font-size: 14px;
  font-family: Inter;
  font-weight: 400;
  word-wrap: break-word;
  text-align: left;
`;

export const PasswordRecoveryPage = () => {
  const spinner = useAppSelector((state) => state.spinner);
  const {
    errors,
    onEmailSubmit,
    onCodeAndPasswordSubmit,
    usernameRegisterProps,
    currentStep,
    resendCode,
    control,
    isPasswordVisible,
    setIsPasswordVisible,
    newPassword,
    navigate,
  } = usePasswordRecoveryPage();

  return FP.match(currentStep)
    .on(
      passwordRecoverySteps.ENTER_EMAIL,
      <AuthContainer>
        <DevIndicator />
        <Stack
          flex={1}
          height={"90vh"}
          gap={5}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <AuthFormContent gap={3}>
            <div className="flex flex-row justify-start items-center">
              <AuthLink to="/" sx={{ color: customColors.grayText }}>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-start"}
                  alignItems={"center"}
                  gap={1}
                  width={"100%"}
                >
                  <ChevronLeftIcon fontSize="large" />
                </Stack>
              </AuthLink>
              <Title>Reset Password</Title>
            </div>
            <form onSubmit={onEmailSubmit} className="mui-form">
              <Stack gap={4}>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="email">Enter email</InputLabel>
                  <Input
                    id="email"
                    type="text"
                    error={!!errors?.username}
                    {...usernameRegisterProps}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="username"
                    render={({ message }) => <Error>{message}</Error>}
                  />
                </Stack>
                <SubmitButton
                  type="submit"
                  variant="contained"
                  disabled={!!spinner}
                  size="large"
                >
                  Send
                </SubmitButton>
              </Stack>
            </form>
          </AuthFormContent>
        </Stack>
        <Box sx={{ height: "10vh" }}></Box>
      </AuthContainer>
    )
    .on(
      passwordRecoverySteps.ENTER_CODE,
      <AuthContainer>
        <Stack gap={5} alignItems={"center"}>
          <AuthFormContent gap={2}>
            <AuthLink to="/" sx={{ color: customColors.grayText }}>
              <Stack
                direction={"row"}
                justifyContent={"flex-start"}
                alignItems={"center"}
                width={"100%"}
              >
                <ChevronLeftIcon fontSize="large" />
                <Box sx={{ fontSize: "20px" }}>Go Back</Box>
              </Stack>
            </AuthLink>
            <div className="flex flex-row justify-start items-center gap-2">
              <EmailIcon />
              <Title>Confirmation Code</Title>
            </div>
            <form onSubmit={onCodeAndPasswordSubmit} className="mui-form">
              <Stack alignItems={"stretch"} gap={"20px"}>
                <Info>We sent a verification code to your email.</Info>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="code">Enter code</InputLabel>
                  <FormMaskedTextField
                    id="code"
                    name="code"
                    control={control}
                    rules={getCodeValidation()}
                    type="text"
                    InputProps={{
                      autoComplete: "newpassword",
                    }}
                    mask={"999999"}
                    inputProps={{
                      autocomplete: "new-password",
                      form: {
                        autocomplete: "new-password",
                      },
                    }}
                  />
                </Stack>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-start"}
                  alignItems={"center"}
                  gap={1}
                >
                  <Question>Didn't get the code?</Question>
                  <AuthButtonLink onClick={resendCode}>Resend</AuthButtonLink>
                </Stack>
                <Stack alignItems={"stretch"} gap={2}>
                  <Stack alignItems={"flex-start"}>
                    <div className="flex flex-row justify-start items-center gap-1">
                      <InputLabel htmlFor="newPassword">
                        Create Password
                      </InputLabel>
                      <HelpIcon>
                        <ul className="list-disc list-inside text-xs text-gray-700 w-fit">
                          <li>Min length is 8</li>
                          <li>Max length is 16 </li>
                          <li>Need one lowercase character</li>
                          <li>Need one uppercase character</li>
                          <li>Need one number character</li>
                          <li>Need one special character (e.g “!@#$%^&*)</li>
                        </ul>
                      </HelpIcon>
                    </div>
                    <FormTextField
                      id="newPassword"
                      name="newPassword"
                      control={control}
                      rules={passwordValidation}
                      type={isPasswordVisible ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            color="info"
                            onClick={() =>
                              setIsPasswordVisible(!isPasswordVisible)
                            }
                          >
                            {isPasswordVisible ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        ),
                        autoComplete: "off",
                      }}
                      inputProps={{
                        autocomplete: "new-password",
                        form: {
                          autocomplete: "new-password",
                        },
                      }}
                    />
                  </Stack>
                  <Stack alignItems={"flex-start"}>
                    <InputLabel htmlFor="passwordRepeat">
                      Confirm New Password
                    </InputLabel>
                    <FormTextField
                      id="passwordRepeat"
                      name="passwordRepeat"
                      control={control}
                      rules={getPasswordConfirmValidation(newPassword.current)}
                      type={isPasswordVisible ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            color="info"
                            onClick={() =>
                              setIsPasswordVisible(!isPasswordVisible)
                            }
                          >
                            {isPasswordVisible ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        ),
                        autoComplete: "off",
                      }}
                      inputProps={{
                        autocomplete: "new-password",
                        form: {
                          autocomplete: "new-password",
                        },
                      }}
                    />
                  </Stack>
                  <SubmitButton
                    type="submit"
                    variant="contained"
                    disabled={!!spinner}
                    size="large"
                  >
                    Reset Password
                  </SubmitButton>
                </Stack>
              </Stack>
            </form>
          </AuthFormContent>
        </Stack>
        <Box sx={{ height: "1%" }}></Box>
      </AuthContainer>
    )
    .on(
      passwordRecoverySteps.SUCCESS,
      <AuthContainer>
        <Stack gap={5} alignItems={"center"}>
          <AuthFormContent gap={3}>
            <EmailIcon />
            <Title>Done!</Title>
            <p>The password has been successfully reset.</p>
            <SubmitButton
              variant="contained"
              disabled={!!spinner}
              size="large"
              onClick={() => navigate("/")}
              sx={{ marginTop: "40px" }}
            >
              Log in
            </SubmitButton>
          </AuthFormContent>
        </Stack>
        <Box sx={{ height: "10vh" }}></Box>
      </AuthContainer>
    )
    .otherwise(null);
};
